import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useLocalStorage } from "@src/appV2/lib/utils/useLocalstorage";
import { useModalState } from "@src/appV2/redesign/system/ui-components";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useCallback, useState } from "react";

import { CancellationProtectionBanner } from "./CancellationProtectionBanner";
import { CancellationProtectionTermsDialog } from "./CancellationProtectionTermsDialog";
import { useCancellationProtectionConfig } from "./hooks/useCancellationProtectionConfig";

const CANCELLATION_PROTECTION_NEARBY_PAYOUTS_AVAILABLE_STORAGE_KEY_PREFIX =
  "cancellation-protection-nearby-payouts-available";

function getNearbyPayoutsStorageKey(workerId: string): string {
  return `${CANCELLATION_PROTECTION_NEARBY_PAYOUTS_AVAILABLE_STORAGE_KEY_PREFIX}-${workerId}`;
}

type TermsDialogInitialTab = "terms" | "payouts";

export function CancellationProtectionBannerEntrypoint() {
  const { userId: workerId } = useDefinedWorker();
  const cancellationProtectionConfig = useCancellationProtectionConfig();
  const isBannerEnabled =
    cancellationProtectionConfig.enabled && cancellationProtectionConfig.isBannerEnabled;
  const termsDialogState = useModalState();

  const storageKey = getNearbyPayoutsStorageKey(workerId);
  const [hasNearbyPayouts, setHasNearbyPayouts] = useLocalStorage<boolean>(storageKey, false);
  const [initialTab, setInitialTab] = useState<TermsDialogInitialTab>("terms");

  useLogEffect(
    APP_V2_USER_EVENTS.CANCELLATION_PROTECTION_BANNER_VIEWED,
    { workerId },
    { enabled: isBannerEnabled }
  );

  const handleViewTerms = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.CANCELLATION_PROTECTION_BANNER_VIEW_TERMS_CLICKED, {
      workerId,
    });
    setInitialTab("terms");
    termsDialogState.openModal();
  }, [workerId, termsDialogState]);

  const handleMeetTheWorkers = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.CANCELLATION_PROTECTION_BANNER_VIEW_RECENT_PROTECTIONS_CLICKED, {
      workerId,
    });
    setInitialTab("payouts");
    termsDialogState.openModal();
  }, [workerId, termsDialogState]);

  const handleNearbyPayoutsAvailableChange = useCallback(
    (available: boolean) => {
      setHasNearbyPayouts(available);
    },
    [setHasNearbyPayouts]
  );

  if (!isBannerEnabled) {
    return null;
  }

  return (
    <>
      <CancellationProtectionBanner
        hasNearbyPayouts={hasNearbyPayouts}
        onViewTerms={handleViewTerms}
        onMeetTheWorkers={handleMeetTheWorkers}
      />
      <CancellationProtectionTermsDialog
        modalState={termsDialogState}
        initialTab={initialTab}
        onNearbyPayoutsAvailableChange={handleNearbyPayoutsAvailableChange}
      />
    </>
  );
}
